/**
 * Phase 3 — Remediation Roadmap horizon grouping (telecom_v2 only).
 *
 * Converts the flat roadmap output into three executive-friendly horizons:
 *   - Immediate     (0–3 months)
 *   - Quick Wins    (3–6 months)
 *   - Strategic     (6–18 months)
 *
 * Grouping is deterministic. Existing rows / fields are preserved; this
 * helper only assigns a horizon label.
 */

export type RoadmapHorizon = "Immediate" | "Quick Wins" | "Strategic";

export const HORIZON_ORDER: RoadmapHorizon[] = ["Immediate", "Quick Wins", "Strategic"];

export const HORIZON_META: Record<RoadmapHorizon, { label: string; window: string; description: string }> = {
  Immediate: {
    label: "Immediate",
    window: "0–3 months",
    description: "Critical exposures and high-priority gaps requiring action within the current quarter.",
  },
  "Quick Wins": {
    label: "Quick Wins",
    window: "3–6 months",
    description: "High-value remediations achievable in the next two quarters with existing capacity.",
  },
  Strategic: {
    label: "Strategic",
    window: "6–18 months",
    description: "Longer-horizon investments to mature controls and sustain resilience.",
  },
};

export interface HorizonInput {
  priority_level?: string | null;
  risk_level?: string | null;
  timeframe?: string | null;
}

/**
 * Deterministic resolution rule (first match wins):
 *   1. priority_level: "Immediate" → Immediate, "Quick Win" → Quick Wins, "Strategic" → Strategic
 *   2. risk_level: Critical → Immediate, High/HIGH → Quick Wins, Moderate/Low → Strategic
 *   3. timeframe parsed in days:
 *        ≤ 90       → Immediate
 *        91 – 180   → Quick Wins
 *        > 180      → Strategic
 *   4. Default → Strategic
 */
export function resolveHorizon(row: HorizonInput): RoadmapHorizon {
  const pl = (row.priority_level ?? "").trim().toLowerCase();
  if (pl === "immediate") return "Immediate";
  if (pl === "quick win" || pl === "quick wins") return "Quick Wins";
  if (pl === "strategic") return "Strategic";

  const rl = (row.risk_level ?? "").trim().toLowerCase();
  if (rl === "critical") return "Immediate";
  if (rl === "high") return "Quick Wins";
  if (rl === "moderate" || rl === "low") return "Strategic";

  const days = parseTimeframeDays(row.timeframe);
  if (days != null) {
    if (days <= 90) return "Immediate";
    if (days <= 180) return "Quick Wins";
    return "Strategic";
  }

  return "Strategic";
}

/** Extract the largest day count from a timeframe string like "90–180 days" or "30 days". */
export function parseTimeframeDays(tf?: string | null): number | null {
  if (!tf) return null;
  const matches = String(tf).match(/\d+/g);
  if (!matches || matches.length === 0) return null;
  const nums = matches.map((m) => parseInt(m, 10)).filter((n) => Number.isFinite(n));
  if (!nums.length) return null;
  return Math.max(...nums);
}

/** Group rows by horizon, preserving original ordering inside each group. */
export function groupByHorizon<T extends HorizonInput>(rows: T[]): Record<RoadmapHorizon, T[]> {
  const out: Record<RoadmapHorizon, T[]> = {
    Immediate: [],
    "Quick Wins": [],
    Strategic: [],
  };
  for (const r of rows) {
    out[resolveHorizon(r)].push(r);
  }
  return out;
}
